import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Skeleton } from '@/components/atoms/Skeleton';
import { useAuthStore } from '@/store/authStore';
import { useScrollLock } from '@/hooks/useScrollLock';

interface UserDetail {
  user: {
    user_id: string;
    nickname: string;
    email: string | null;
    level: number;
    score: number;
    admin_role: string | null;
    sanction_type: string | null;
    sanction_until: string | null;
    warning_count: number;
    post_count: number;
    comment_count: number;
    is_premium: boolean;
    is_deleted: boolean;
    created_at: string;
    last_active_at: string | null;
    deleted_at: string | null;
  };
  sanctions: Array<{
    id: number;
    sanction_type: string;
    reason: string | null;
    reason_category: string | null;
    notify_message: string | null;
    duration_days: number | null;
    starts_at: string;
    ends_at: string | null;
    is_active: boolean;
    released_at: string | null;
    is_auto: boolean;
    created_at: string;
    admin_nickname: string | null;
  }>;
  reports: unknown[];
  recentPosts: Array<{
    id: number;
    title: string;
    board_id: number;
    is_blinded: boolean;
    is_deleted: boolean;
    view_count: number;
    like_count: number;
    created_at: string;
  }>;
}

const REASON_CATEGORIES = [
  { value: 'abuse', label: '욕설/비방' },
  { value: 'spam', label: '스팸/광고' },
  { value: 'flooding', label: '도배' },
  { value: 'impersonation', label: '사칭' },
  { value: 'copyright', label: '저작권' },
  { value: 'other', label: '기타' },
];

function fmtDateTime(iso: string | null) {
  if (!iso) return '-';
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16);
}

type ModalType = 'suspend' | 'role' | 'delete' | null;

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = id!;
  const me = useAuthStore((s) => s.me);
  const isSuper = me?.adminRole === 'super';
  const qc = useQueryClient();

  const [modal, setModal] = useState<ModalType>(null);
  useScrollLock(modal !== null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => get<UserDetail>(`/admin/users/${userId}`),
  });

  const refetch = () => qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });

  // ── 정지 폼
  const [suspendForm, setSuspendForm] = useState({
    duration_days: 7 as number | null,
    is_permanent: false,
    reason_category: 'abuse',
    reason_memo: '',
    notify_message: '',
  });

  const suspendMutation = useMutation({
    mutationFn: () =>
      post(`/admin/users/${userId}/suspend`, {
        duration_days: suspendForm.is_permanent ? null : suspendForm.duration_days,
        reason_category: suspendForm.reason_category,
        reason_memo: suspendForm.reason_memo,
        notify_message: suspendForm.notify_message || undefined,
      }),
    onSuccess: () => {
      setModal(null);
      refetch();
    },
  });

  const unsuspendMutation = useMutation({
    mutationFn: () => post(`/admin/users/${userId}/unsuspend`),
    onSuccess: () => refetch(),
  });

  // ── 권한 폼
  const [roleForm, setRoleForm] = useState<string>('readonly');
  const roleMutation = useMutation({
    mutationFn: () =>
      post(`/admin/users/${userId}/role`, {
        admin_role: roleForm === '' ? null : roleForm,
      }),
    onSuccess: () => {
      setModal(null);
      refetch();
    },
  });

  // ── 탈퇴 폼
  const [deleteReason, setDeleteReason] = useState('');
  const deleteMutation = useMutation({
    mutationFn: () => post(`/admin/users/${userId}/delete`, { reason_memo: deleteReason }),
    onSuccess: () => {
      setModal(null);
      refetch();
    },
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;
  const u = data.user;
  const hasActiveSanction =
    u.sanction_type === 'ban' ||
    (u.sanction_until && new Date(u.sanction_until) > new Date());

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/users" className="text-sm text-[var(--color-text-sub)] hover:underline">
          ← 목록으로
        </Link>
      </div>

      {/* 헤더 카드 */}
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-extrabold text-[var(--color-text)]">{u.nickname}</h1>
          {u.admin_role && (
            <Badge variant={u.admin_role === 'super' ? 'premium' : 'primary'}>
              {u.admin_role === 'readonly' ? 'viewer' : u.admin_role}
            </Badge>
          )}
          {u.is_deleted && <Badge variant="fall">탈퇴</Badge>}
          {u.sanction_type === 'ban' && <Badge variant="fall">영구정지</Badge>}
          {hasActiveSanction && u.sanction_type !== 'ban' && <Badge variant="fall">정지중</Badge>}
          {u.is_premium && <Badge variant="premium">프리미엄</Badge>}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">user_id</div>
            <div className="font-mono text-xs">{u.user_id}</div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">이메일</div>
            <div>{u.email ?? '-'}</div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">레벨/점수</div>
            <div>
              Lv {u.level} · {u.score}점
            </div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">경고</div>
            <div>{u.warning_count}회</div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">가입일</div>
            <div>{fmtDateTime(u.created_at)}</div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">최근접속</div>
            <div>{fmtDateTime(u.last_active_at)}</div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">정지만료</div>
            <div>{fmtDateTime(u.sanction_until)}</div>
          </div>
          <div>
            <div className="text-[var(--color-text-sub)] text-xs">글/댓글</div>
            <div>
              {u.post_count}/{u.comment_count}
            </div>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="mt-5 flex flex-wrap gap-2">
          {hasActiveSanction ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={!isSuper}
              loading={unsuspendMutation.isPending}
              onClick={() => unsuspendMutation.mutate()}
            >
              정지 해제
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              disabled={!isSuper || u.is_deleted}
              onClick={() => setModal('suspend')}
            >
              정지/영구정지
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={!isSuper || u.is_deleted}
            onClick={() => {
              setRoleForm(u.admin_role ?? '');
              setModal('role');
            }}
          >
            권한 변경
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={!isSuper || u.is_deleted}
            onClick={() => setModal('delete')}
          >
            강제 탈퇴
          </Button>
          {!isSuper && (
            <span className="self-center text-xs text-[var(--color-text-sub)]">
              액션은 super 권한만 가능
            </span>
          )}
        </div>
      </section>

      {/* 정지 이력 */}
      <section>
        <h2 className="mb-3 text-lg font-bold">정지 이력 ({data.sanctions.length})</h2>
        {data.sanctions.length === 0 ? (
          <p className="text-sm text-[var(--color-text-sub)]">기록이 없어요.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg)] text-left">
                <tr>
                  <th className="p-3">유형</th>
                  <th className="p-3">사유</th>
                  <th className="p-3">기간</th>
                  <th className="p-3">처리자</th>
                  <th className="p-3">시작</th>
                  <th className="p-3">종료</th>
                  <th className="p-3">상태</th>
                </tr>
              </thead>
              <tbody>
                {data.sanctions.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--color-border)]">
                    <td className="p-3">{s.sanction_type}</td>
                    <td className="p-3">
                      {s.reason_category ?? '-'}
                      {s.reason ? (
                        <div className="mt-0.5 text-xs text-[var(--color-text-sub)]">
                          {s.reason}
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3">{s.duration_days ? `${s.duration_days}일` : '영구'}</td>
                    <td className="p-3">{s.admin_nickname ?? (s.is_auto ? '자동' : '-')}</td>
                    <td className="p-3 text-xs text-[var(--color-text-sub)]">
                      {fmtDateTime(s.starts_at)}
                    </td>
                    <td className="p-3 text-xs text-[var(--color-text-sub)]">
                      {fmtDateTime(s.ends_at)}
                    </td>
                    <td className="p-3">
                      {s.released_at ? (
                        <Badge variant="default">해제됨</Badge>
                      ) : s.is_active ? (
                        <Badge variant="fall">활성</Badge>
                      ) : (
                        <Badge variant="default">만료</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 최근 게시글 */}
      <section>
        <h2 className="mb-3 text-lg font-bold">최근 게시글 ({data.recentPosts.length})</h2>
        {data.recentPosts.length === 0 ? (
          <p className="text-sm text-[var(--color-text-sub)]">없어요.</p>
        ) : (
          <ul className="divide-y divide-[var(--color-border)] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
            {data.recentPosts.map((p) => (
              <li key={p.id} className="flex items-center gap-3 p-3 text-sm">
                <Link to={`/post/${p.id}`} className="flex-1 font-medium hover:underline">
                  {p.title}
                </Link>
                {p.is_blinded && <Badge variant="fall">블라인드</Badge>}
                {p.is_deleted && <Badge variant="default">삭제</Badge>}
                <span className="text-xs text-[var(--color-text-sub)]">
                  {fmtDateTime(p.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ━━━ 모달들 ━━━ */}
      {modal === 'suspend' && (
        <Modal title={`${u.nickname} 정지`} onClose={() => setModal(null)}>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={suspendForm.is_permanent}
                onChange={(e) =>
                  setSuspendForm((f) => ({ ...f, is_permanent: e.target.checked }))
                }
              />
              영구정지
            </label>
            {!suspendForm.is_permanent && (
              <Input
                type="number"
                min={1}
                max={365}
                value={suspendForm.duration_days ?? 7}
                onChange={(e) =>
                  setSuspendForm((f) => ({ ...f, duration_days: Number(e.target.value) }))
                }
                placeholder="정지 기간(일)"
              />
            )}
            <select
              value={suspendForm.reason_category}
              onChange={(e) =>
                setSuspendForm((f) => ({ ...f, reason_category: e.target.value }))
              }
              className="h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm"
            >
              {REASON_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <Textarea
              rows={3}
              value={suspendForm.reason_memo}
              onChange={(e) =>
                setSuspendForm((f) => ({ ...f, reason_memo: e.target.value }))
              }
              placeholder="사유 메모 (필수, 2자 이상)"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
            />
            <Textarea
              rows={2}
              value={suspendForm.notify_message}
              onChange={(e) =>
                setSuspendForm((f) => ({ ...f, notify_message: e.target.value }))
              }
              placeholder="유저 알림 메시지 (선택)"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              취소
            </Button>
            <Button
              fullWidth
              variant="danger"
              loading={suspendMutation.isPending}
              disabled={suspendForm.reason_memo.trim().length < 2}
              onClick={() => suspendMutation.mutate()}
            >
              정지 적용
            </Button>
          </div>
        </Modal>
      )}

      {modal === 'role' && (
        <Modal title="권한 변경" onClose={() => setModal(null)}>
          <select
            value={roleForm}
            onChange={(e) => setRoleForm(e.target.value)}
            className="h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm"
          >
            <option value="">없음 (일반 유저)</option>
            <option value="readonly">viewer (조회 전용)</option>
            <option value="content">content (운영)</option>
            <option value="super">super (최고 관리자)</option>
          </select>
          <p className="mt-2 text-xs text-[var(--color-text-sub)]">
            현재: {u.admin_role ?? '없음'}
          </p>
          <div className="mt-4 flex gap-2">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              취소
            </Button>
            <Button
              fullWidth
              loading={roleMutation.isPending}
              onClick={() => roleMutation.mutate()}
            >
              권한 변경 적용
            </Button>
          </div>
        </Modal>
      )}

      {modal === 'delete' && (
        <Modal title={`${u.nickname} 강제 탈퇴`} onClose={() => setModal(null)}>
          <p className="text-sm text-[var(--color-rise)]">
            ⚠️ soft delete 입니다. 유저 프로필이 비활성화되며 데이터는 보존됩니다.
          </p>
          <Textarea
            rows={3}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="탈퇴 사유 (감사 로그용, 2자 이상)"
            className="mt-3 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
          />
          <div className="mt-4 flex gap-2">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              취소
            </Button>
            <Button
              fullWidth
              variant="danger"
              loading={deleteMutation.isPending}
              disabled={deleteReason.trim().length < 2}
              onClick={() => deleteMutation.mutate()}
            >
              강제 탈퇴
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
      >
        <h3 className="mb-3 text-lg font-bold text-[var(--color-text)]">{title}</h3>
        {children}
      </div>
    </div>
  );
}
